import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { RootState } from "../../store";

import {
    Attachment,
    Client,
    ClientWithDeal,
    Comment,
    Deal,
    DealStatusCatalog,
    DealStatusId,
    ClientUpsertPayload,
    ClientSortBy,
    ClientPaginationMeta,
} from "./types";
import { ClientList } from "./ClientList";
import { EMPTY_STATE_TEXT, CLIENT_DEAL_STATUSES } from "./constants";
import { computeStats, filterClients, joinClientData } from "./utils";

import { AddClientModal } from "./AddClientModal";
import { DeleteClientModal } from "./DeleteClientModal";
import { UserActionCreator } from "../../store/userActions.ts";

import {
    selectCreateClientPending,
    selectCreateClientFieldErrors,
    selectCreateClientErrorMessage,
    selectCreateClientSuccess,
    selectClientsBaseList,
    selectClientsBasePending,
    selectClientsBaseError,
    selectClientsBaseErrorMessage,
    selectClientsBaseLoadingMore,
    selectClientsBaseMeta,
    selectIsAuth,
    selectDealStatusCatalog,
    selectDealStatusesCountsById,
} from "../../store/selectors.ts";

import { FiltersBar } from "./FiltersBar";
import { StatsPanel } from "./StatsPanel";
import { FunnelPanel, FunnelItem } from "./FunnelPanel";

type ClientFiltersState = {
    search: string;
    /**
     * Пустой массив = все статусы.
     * Можем выбирать несколько статусов одновременно.
     */
    statuses: DealStatusId[];
};

type StateProps = {
    clients: Client[];
    deals: Deal[];
    comments: Comment[];
    attachments: Attachment[];
    catalog: DealStatusCatalog;
    statusCountsById: Record<string, number>;

    clientsPending: boolean;
    clientsError: boolean;
    clientsErrorMessage: string | null;
    clientsLoadingMore: boolean;
    clientsMeta: ClientPaginationMeta;

    createClientPending: boolean;
    createClientFieldErrors: Record<string, string>;
    createClientErrorMessage: string | null;
    createClientSuccess: boolean;

    isAuth: boolean;
};

type DispatchProps = {
    createClientInBase: (payload: ClientUpsertPayload) => any;
    updateClientInBase: (payload: {
        clientId: string | number;
        data: ClientUpsertPayload;
    }) => any;
    deleteClientFromBase: (payload: { clientId: string | number }) => any;
    getClientsBase: (payload?: {
        dealStatusIds?: (number | string)[];
        sortBy?: ClientSortBy;
        perPage?: number;
        periodFrom?: string;
        periodTo?: string;
        cursor?: string | null;
    }) => any;
    getDealStatuses: () => any;
};

type OwnProps = { onOpenClient?: (id: string | number) => void };

type Props = StateProps & DispatchProps & OwnProps;

const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getCurrentMonthPeriod = () => {
    const now = new Date();

    return {
        periodFrom: formatDateForInput(
            new Date(now.getFullYear(), now.getMonth(), 1),
        ),
        periodTo: formatDateForInput(
            new Date(now.getFullYear(), now.getMonth() + 1, 0),
        ),
    };
};

const _ClientBasePage: React.FC<Props> = ({
                                              clients,
                                              deals,
                                              comments,
                                              attachments,
                                              catalog,
                                              statusCountsById,
                                              onOpenClient,
                                              createClientInBase,
                                              updateClientInBase,
                                              deleteClientFromBase,
                                              getClientsBase,
                                              getDealStatuses,
                                              clientsPending,
                                              clientsError,
                                              clientsErrorMessage,
                                              clientsLoadingMore,
                                              clientsMeta,
                                              createClientPending,
                                              createClientFieldErrors,
                                              createClientErrorMessage,
                                              createClientSuccess,
                                              isAuth,
                                          }) => {
    const [filters, setFilters] = useState<ClientFiltersState>({
        search: "",
        statuses: [],
    });
    const [sortBy, setSortBy] = useState<ClientSortBy>("fullname");
    const [period, setPeriod] = useState(getCurrentMonthPeriod);
    const periodIsValid =
        Boolean(period.periodFrom) &&
        Boolean(period.periodTo) &&
        period.periodFrom <= period.periodTo;

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState<ClientWithDeal | null>(null);
    const [deletingClient, setDeletingClient] = useState<ClientWithDeal | null>(null);
    const [updatePending, setUpdatePending] = useState(false);
    const [updateErrorMessage, setUpdateErrorMessage] = useState<string | null>(null);
    const [updateFieldErrors, setUpdateFieldErrors] = useState<Record<string, string>>({});
    const [deletePending, setDeletePending] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

    // ─────────────────────────────── Загрузка статусов сделок
    useEffect(() => {
        if (!isAuth) return;

        try {
            const maybePromise = getDealStatuses();

            if (maybePromise && typeof (maybePromise as any).then === "function") {
                (maybePromise as any)
                    .then((action: any) => {
                        console.log("[ClientBasePage] deal statuses action:", action);
                        console.log("[ClientBasePage] deal statuses payload:", action?.payload);
                    })
                    .catch((err: any) => {
                        console.error("[ClientBasePage] deal statuses error:", err);
                    });
            } else {
                console.log(
                    "[ClientBasePage] getDealStatuses dispatched (без промиса)",
                    maybePromise,
                );
            }
        } catch (err) {
            console.error("[ClientBasePage] deal statuses call failed:", err);
        }
    }, [isAuth, getDealStatuses]);

    // ─────────────────────────────── Сбор dealStatusIds для бэка
    const buildDealStatusIds = (): number[] => {
        // пустой массив = "все статусы" → бэку отдаём []
        if (!filters.statuses.length) return [];

        const ids: number[] = [];

        filters.statuses.forEach((value) => {
            const statusFromCatalog = catalog.find(
                (s) => s.code === value || s.id === value,
            );

            if (statusFromCatalog) {
                ids.push(Number(statusFromCatalog.id));
            }
        });

        return ids;
    };

    // ─────────────────────────────── Загрузка клиентов при изменении фильтров
    useEffect(() => {
        if (!isAuth) return;
        if (!periodIsValid) return;

        const dealStatusIds = buildDealStatusIds();
        getClientsBase({
            dealStatusIds,
            sortBy,
            perPage: 20,
            periodFrom: period.periodFrom,
            periodTo: period.periodTo,
            cursor: null,
        });
    }, [
        isAuth,
        filters.statuses,
        sortBy,
        period.periodFrom,
        period.periodTo,
        periodIsValid,
        getClientsBase,
    ]);

    // после успешного добавления клиента перегружаем список с теми же фильтрами
    useEffect(() => {
        if (!isAuth) return;
        if (createClientSuccess && !createClientPending) {
            setIsAddModalOpen(false);
            if (!periodIsValid) return;
            const dealStatusIds = buildDealStatusIds();
            getClientsBase({
                dealStatusIds,
                sortBy,
                perPage: 20,
                periodFrom: period.periodFrom,
                periodTo: period.periodTo,
                cursor: null,
            });
        }
    }, [
        createClientSuccess,
        createClientPending,
        getClientsBase,
        isAuth,
        filters.statuses,
        sortBy,
        period.periodFrom,
        period.periodTo,
        periodIsValid,
    ]);

    // ─────────────────────────────── Нормализация клиентов
    const normalizedClients = useMemo(() => {
        return (clients || []).map((c: any) => {
            const socials = (c.socials || []) as Array<{ type: string; value: string }>;
            const telegramFromSocials =
                socials.find((s) => s.type === "telegram")?.value ?? null;
            const whatsappFromSocials =
                socials.find((s) => s.type === "whatsapp")?.value ?? null;
            const instagramFromSocials =
                socials.find((s) => s.type === "instagram")?.value ?? null;

            return {
                ...c,
                fullName: (c.fullName ?? c.fullname ?? "").trim(),
                phone: String(c.phone ?? ""),
                telegram: c.telegram ?? telegramFromSocials,
                whatsapp: c.whatsapp ?? whatsappFromSocials,
                instagram: c.instagram ?? instagramFromSocials,
            };
        });
    }, [clients]);

    // объединяем клиента, сделки, комментарии и вложения
    const rows: ClientWithDeal[] = useMemo(
        () => joinClientData(normalizedClients as any, deals, comments, attachments),
        [normalizedClients, deals, comments, attachments],
    );

    // фильтрация только по поиску — статус уже учтён на бэке
    const filtered = useMemo(
        () =>
            filterClients(rows, {
                search: filters.search,
                status: "all",
            }),
        [rows, filters.search],
    );

    const stats = useMemo(() => computeStats(filtered), [filtered]);

    // ─────────────────────────────── Воронка: каталог + счётчики бэка
    const funnel: FunnelItem[] = useMemo(
        () =>
            catalog.map((status) => ({
                id: status.id,
                code: status.code,
                label: status.name,
                color: status.color || "#4B9CE2",
                count: statusCountsById[String(status.id)] ?? 0,
            })),
        [catalog, statusCountsById],
    );

    // ─────────────────────────────── Обработчики
    const handleCreateClient = (payload: ClientUpsertPayload) => {
        createClientInBase(payload);
    };

    const reloadClients = () => {
        if (!periodIsValid) return Promise.resolve();

        return getClientsBase({
            dealStatusIds: buildDealStatusIds(),
            sortBy,
            perPage: 20,
            periodFrom: period.periodFrom,
            periodTo: period.periodTo,
            cursor: null,
        });
    };

    const reloadAfterDealChange = async () => {
        await Promise.all([
            reloadClients(),
            getDealStatuses(),
        ]);
    };

    const handleOpenEditClient = (client: ClientWithDeal) => {
        setUpdateErrorMessage(null);
        setUpdateFieldErrors({});
        setEditingClient(client);
    };

    const handleUpdateClient = async (data: ClientUpsertPayload) => {
        if (!editingClient) return;

        setUpdatePending(true);
        setUpdateErrorMessage(null);
        setUpdateFieldErrors({});

        try {
            const action = await updateClientInBase({
                clientId: editingClient.id,
                data,
            });

            if (action?.type?.endsWith("/fulfilled")) {
                setEditingClient(null);
                reloadClients();
                return;
            }

            setUpdateErrorMessage(
                action?.payload?.message ||
                "Не удалось сохранить изменения. Попробуйте ещё раз.",
            );
            setUpdateFieldErrors(action?.payload?.errors || {});
        } finally {
            setUpdatePending(false);
        }
    };

    const handleOpenDeleteClient = (client: ClientWithDeal) => {
        setDeleteErrorMessage(null);
        setDeletingClient(client);
    };

    const handleDeleteClient = async () => {
        if (!deletingClient) return;

        setDeletePending(true);
        setDeleteErrorMessage(null);

        try {
            const action = await deleteClientFromBase({
                clientId: deletingClient.id,
            });

            if (action?.type?.endsWith("/fulfilled")) {
                setDeletingClient(null);
                reloadClients();
                return;
            }

            setDeleteErrorMessage(
                action?.payload?.message ||
                "Не удалось удалить клиента. Попробуйте ещё раз.",
            );
        } finally {
            setDeletePending(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setFilters((prev) => ({ ...prev, search: value }));
    };

    const handleStatusesChange = (values: DealStatusId[]) => {
        setFilters((prev) => ({ ...prev, statuses: values }));
    };

    const handleLoadMore = () => {
        if (
            clientsPending ||
            !periodIsValid ||
            !clientsMeta.hasMore ||
            !clientsMeta.nextCursor ||
            clientsLoadingMore
        ) {
            return;
        }

        getClientsBase({
            dealStatusIds: buildDealStatusIds(),
            sortBy,
            perPage: clientsMeta.perPage || 20,
            periodFrom: period.periodFrom,
            periodTo: period.periodTo,
            cursor: clientsMeta.nextCursor,
        });
    };

    // ─────────────────────────────── Render
    return (
        <div className="p-4 space-y-4">
            <FiltersBar
                search={filters.search}
                statuses={filters.statuses}
                sortBy={sortBy}
                periodFrom={period.periodFrom}
                periodTo={period.periodTo}
                catalog={catalog}
                isAuth={isAuth}
                onChangeSearch={handleSearchChange}
                onChangeStatuses={handleStatusesChange}
                onChangeSortBy={setSortBy}
                onChangePeriodFrom={(periodFrom) =>
                    setPeriod((current) => ({ ...current, periodFrom }))
                }
                onChangePeriodTo={(periodTo) =>
                    setPeriod((current) => ({ ...current, periodTo }))
                }
                onOpenAddModal={() => setIsAddModalOpen(true)}
            />

            {!isAuth && (
                <div className="text-sm text-gray-500">
                    Войдите в аккаунт, чтобы увидеть базу клиентов.
                </div>
            )}

            {isAuth && clientsPending && (
                <div className="text-sm text-gray-500">Загружаем клиентов…</div>
            )}

            {isAuth && clientsError && !clientsPending && (
                <div className="text-sm text-red-500">
                    Ошибка при загрузке клиентов:{" "}
                    {clientsErrorMessage || "Не удалось получить данные"}
                </div>
            )}

            <StatsPanel stats={stats} />

            <FunnelPanel
                funnel={funnel}
                selectedStatuses={filters.statuses}
                onChangeStatuses={handleStatusesChange}
            />

            <div className="border rounded p-3">
                <div className="text-sm font-medium mb-2">Клиенты</div>
                {filtered.length === 0 ? (
                    <div className="text-sm text-gray-500">{EMPTY_STATE_TEXT}</div>
                ) : (
                    <ClientList
                        rows={filtered}
                        catalog={catalog}
                        onOpenClient={onOpenClient}
                        onEditClient={handleOpenEditClient}
                        onDeleteClient={handleOpenDeleteClient}
                        onDealsChanged={reloadAfterDealChange}
                    />
                )}

                {clientsMeta.hasMore && (
                    <div className="flex justify-center border-t pt-3">
                        <button
                            type="button"
                            onClick={handleLoadMore}
                            disabled={clientsLoadingMore}
                            className="rounded-md border border-[#c06198] px-4 py-2 text-sm font-medium text-[#c06198] transition-colors hover:bg-[#c06198]/10 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {clientsLoadingMore ? "Загружаем…" : "Показать ещё"}
                        </button>
                    </div>
                )}
            </div>

            <AddClientModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                pending={createClientPending}
                fieldErrors={createClientFieldErrors}
                errorMessage={createClientErrorMessage}
                onSubmit={handleCreateClient}
            />

            <AddClientModal
                isOpen={Boolean(editingClient)}
                onClose={() => {
                    if (!updatePending) setEditingClient(null);
                }}
                mode="edit"
                initialValues={
                    editingClient
                        ? {
                            fullname: editingClient.fullName,
                            phone: String(editingClient.phone ?? ""),
                            telegram: editingClient.telegram,
                            whatsapp: editingClient.whatsapp,
                            instagram: editingClient.instagram,
                        }
                        : undefined
                }
                pending={updatePending}
                fieldErrors={updateFieldErrors}
                errorMessage={updateErrorMessage}
                onSubmit={handleUpdateClient}
            />

            <DeleteClientModal
                isOpen={Boolean(deletingClient)}
                clientName={deletingClient?.fullName || ""}
                pending={deletePending}
                errorMessage={deleteErrorMessage}
                onClose={() => {
                    if (!deletePending) setDeletingClient(null);
                }}
                onConfirm={handleDeleteClient}
            />
        </div>
    );
};

const mapStateToProps = (state: RootState): StateProps => {
    const backendCatalog = selectDealStatusCatalog(state);

    return {
        clients: selectClientsBaseList(state),

        deals: [] as Deal[],
        comments: [] as Comment[],
        attachments: [] as Attachment[],

        // если с бэка ничего не пришло — используем хардкод
        catalog:
            backendCatalog && backendCatalog.length > 0
                ? backendCatalog
                : CLIENT_DEAL_STATUSES,

        statusCountsById: selectDealStatusesCountsById(state),

        clientsPending: selectClientsBasePending(state),
        clientsError: selectClientsBaseError(state),
        clientsErrorMessage: selectClientsBaseErrorMessage(state),
        clientsLoadingMore: selectClientsBaseLoadingMore(state),
        clientsMeta: selectClientsBaseMeta(state),

        createClientPending: selectCreateClientPending(state),
        createClientFieldErrors: selectCreateClientFieldErrors(state),
        createClientErrorMessage: selectCreateClientErrorMessage(state),
        createClientSuccess: selectCreateClientSuccess(state),

        isAuth: selectIsAuth(state),
    };
};

const mapDispatchToProps: DispatchProps = {
    createClientInBase: UserActionCreator.createClientInBase as any,
    updateClientInBase: UserActionCreator.updateClientInBase as any,
    deleteClientFromBase: UserActionCreator.deleteClientFromBase as any,
    getClientsBase: UserActionCreator.getClientsBase as any,
    getDealStatuses: UserActionCreator.getDealStatuses as any,
};

export const ClientBasePage = connect(
    mapStateToProps,
    mapDispatchToProps,
)(_ClientBasePage);
